import React from "react";
import {
  Grid,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Tooltip,
  Paper,
  withStyles,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import {
  getSummaryTableColumns,
  SUMMARY_TABLE_COLUMNS,
} from "../arrobaBalanceUtil";
import { useTranslations } from "adapters/i18n";

const ArrobaBalanceSummaryTable = ({ classes, farmBalances }) => {
  const { t } = useTranslations();

  const tableColumns = getSummaryTableColumns(t);

  const amountRowLabel = [
    t("common:amount").toUpperCase(),
    `(${t("common:measure.heads")})`,
  ].join(" ");
  const weightRowLabel = [
    t("livestock:weight").toUpperCase(),
    `(${t("common:measure.arroba")})`,
  ].join(" ");

  return (
    <Grid>
      <Typography className={classes.title}>
        {t("arrobaBalance:balance-summary")}
      </Typography>
      <Paper className={classes.tableContainer}>
        <Table id="balance-summary-table" className={classes.table}>
          <TableHead
            data-testid="balance-summary-table-header"
            className={classes.tableHeader}
          >
            <TableRow>
              {tableColumns.map((column) => (
                <TableHeaderCell
                  key={column.id}
                  classes={classes}
                  columnId={column.id}
                  label={column.label}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody data-testid="balance-summary-table-body">
            <TableRow className={classes.tableRow}>
              <TableCell className={classes.tableRow}>
                {amountRowLabel}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.EstoqueInicial.Quantidade}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Entrada.Quantidade}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Saida.Quantidade}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Nascimentos.Quantidade}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Mortes.Quantidade}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.EstoqueFinal.Quantidade}
              </TableCell>
              <TableCell
                rowSpan={2}
                className={classes.tableRowTotalProductionCell}
              >
                {farmBalances.Total}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableRow}>
                {weightRowLabel}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.EstoqueInicial.Peso.toFixed(1)}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Entrada.Peso}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Saida.Peso}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Nascimentos.Peso}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.Mortes.Peso}
              </TableCell>
              <TableCell className={classes.tableRowCell}>
                {farmBalances.EstoqueFinal.Peso.toFixed(1)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Grid>
  );
};

const TableHeaderCell = ({ classes, columnId, label }) => {
  const { t } = useTranslations();

  const tooltipTextMap = {
    [SUMMARY_TABLE_COLUMNS.INITIAL_STOCK]: t(
      "arrobaBalance:initial-stock-disclaimer"
    ),
    [SUMMARY_TABLE_COLUMNS.ENTRIES]: t("arrobaBalance:entry-exit-disclaimer"),
    [SUMMARY_TABLE_COLUMNS.EXITS]: t("arrobaBalance:entry-exit-disclaimer"),
    [SUMMARY_TABLE_COLUMNS.FINAL_STOCK]: t(
      "arrobaBalance:final-stock-disclaimer"
    ),
    [SUMMARY_TABLE_COLUMNS.TOTAL_ARROBA_PRODUCTION]: t(
      "arrobaBalance:total-arrobas-production-disclaimer"
    ),
  };

  const cellClassesMap = {
    [SUMMARY_TABLE_COLUMNS.ROW_DESCRIPTION]: classes.headerRowDescriptionCell,
    [SUMMARY_TABLE_COLUMNS.INITIAL_STOCK]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.ENTRIES]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.EXITS]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.BIRTHS]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.DEATHS]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.FINAL_STOCK]: classes.headerCell,
    [SUMMARY_TABLE_COLUMNS.TOTAL_ARROBA_PRODUCTION]:
      classes.headerProductionCell,
  };

  const tooltipText = tooltipTextMap[columnId];
  const cellClass = cellClassesMap[columnId];

  const tooltipIconColor =
    columnId === SUMMARY_TABLE_COLUMNS.TOTAL_ARROBA_PRODUCTION
      ? "inherit"
      : "primary";

  return (
    <TableCell className={cellClass}>
      <div className={classes.headerCellContent}>
        {label}
        {tooltipText != null && (
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            title={tooltipText}
            placement="top"
          >
            <InfoIcon
              color={tooltipIconColor}
              className={classes.tooltipInfoIcon}
              placement="top"
            />
          </Tooltip>
        )}
      </div>
    </TableCell>
  );
};

const styles = (theme) => {
  const defaultHeaderCell = {
    fontSize: "14px",
    fontWeight: "500",
    letterSpacing: "1px",
    textAlign: "center",
    textTransform: "uppercase",
  };

  const defaultRowCell = {
    fontWeight: "500",
    padding: "4px 24px 4px 24px",
    textAlign: "center",
  };

  return {
    title: {
      fontSize: "20px",
      fontWeight: "bold",
      fontStyle: "medium",
      fontStretch: "medium",
      lineHeight: "medium",
      letterSpacing: "medium",
      color: "#333333",
      marginTop: "40px",
      marginBottom: "20px",
    },
    tableContainer: {
      borderRadius: "16px",
    },
    table: {
      width: "100%",
      fontFamily: "Roboto",
      textAlign: "center",
      padding: "4px 24px 4px 24px",
      fontSize: "14px",
      borderRadius: "16px",
    },
    tableHeader: {
      marginTop: "25px",
      marginBottom: "25px",
      padding: "4px 24px 4px 24px",
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      backgroundColor: "#e1e1e1",
      borderRadius: "16px",
    },
    headerRowDescriptionCell: {
      ...defaultHeaderCell,
      backgroundColor: "#e1e1e1",
      borderRadius: "16px 0 0 0",
      color: theme.palette.primary.main,
      padding: "4px 24px 4px 24px",
    },
    headerCell: {
      ...defaultHeaderCell,
      color: theme.palette.primary.main,
      padding: "4px 17px 4px 17px",
    },
    headerProductionCell: {
      ...defaultHeaderCell,
      backgroundColor: theme.palette.primary.main,
      borderRadius: "0 16px 0 0",
      color: "#fff",
      paddingBottom: "4px",
      paddingTop: "4px",
    },
    headerCellContent: {
      display: "flex",
      justifyContent: "center",
      height: "inherit",
      alignItems: "center",
      textTransform: "uppercase",
    },
    tooltip: {
      fontSize: "10px",
      padding: "8px",
      borderRadius: "4px",
      color: "#ffffff",
      backgroundColor: "#000000BF",
    },
    tooltipInfoIcon: {
      fontSize: "14px",
      position: "relative",
    },
    tableRow: {
      borderBottom: "1px solid #e5e5ea",
      color: theme.palette.primary.main,
      padding: "4px 4px",
      fontSize: "14px",
      fontWeight: "500",
      letterSpacing: "1px",
      textAlign: "center",
      width: "15%",
    },
    tableRowCell: {
      ...defaultRowCell,
      color: "#666",
      fontSize: "18px",
      letterSpacing: "1px",
    },
    tableRowTotalProductionCell: {
      ...defaultRowCell,
      borderLeft: "1px solid #e5e5ea",
      borderRadius: "0 0 0 16px",
      color: theme.palette.primary.main,
      fontSize: "21px",
      lineHeight: "normal",
    },
  };
};

export default withStyles(styles)(ArrobaBalanceSummaryTable);
